import { Link } from "react-router-dom";
import GlassNav from "@/components/GlassNav";
import { ArrowRight, Camera, Heart, QrCode, Sparkles } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen relative">
      <GlassNav />
      <main className="pt-28 pb-24 px-4 max-w-6xl mx-auto">
        <section className="grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
          <div className="slide-up">
            <div className="inline-flex items-center gap-2 glass-subtle px-4 py-2 mb-6" data-testid="hero-badge">
              <Sparkles className="w-4 h-4" style={{ color: "var(--accent)" }} />
              <span className="text-xs uppercase tracking-[0.15em] text-neutral-600">Wedding · QR · Memories</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-neutral-900">
              Every guest.<br />
              Every glance.<br />
              <span className="font-script text-6xl sm:text-7xl" style={{ color: "var(--accent)" }}>every wish.</span>
            </h1>
            <p className="mt-6 max-w-xl text-neutral-600 text-base sm:text-lg">
              A quietly beautiful platform for weddings. Design your invitation, print a single QR — and let every guest add a photo, a video, a wish. All in one gallery you'll keep forever.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-cta-signup" className="btn-accent rounded-full px-6 py-3 inline-flex items-center gap-2">
                Create your wedding page <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/w/sarah-john" data-testid="hero-cta-demo" className="btn-ghost-glass rounded-full px-6 py-3 inline-flex items-center gap-2">
                See a live wedding
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              {[
                { icon: QrCode, label: "One QR, endless memories" },
                { icon: Camera, label: "Photos & videos" },
                { icon: Heart, label: "A wish per photo" },
              ].map(({ icon: Ic, label }, i) => (
                <div key={i} className="glass-subtle p-3 text-xs text-neutral-700">
                  <Ic className="w-4 h-4 mb-1" style={{ color: "var(--accent)" }} />
                  {label}
                </div>
              ))}
            </div>
          </div>
          <div className="fade-in">
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
              <img
                src="https://images.unsplash.com/photo-1780541952163-d9ad11e8ce82?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
                alt="Wedding cover"
                className="absolute inset-0 w-full h-full object-cover rounded-[36px] shadow-2xl"
              />
              <div className="absolute -left-6 -bottom-6 glass p-4 w-56 gloss-highlight">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Latest wish</div>
                <div className="font-serif text-neutral-800 text-lg leading-tight">"May your love keep growing, always."</div>
                <div className="text-xs text-neutral-500 mt-2">— Amara</div>
              </div>
              <div className="absolute -right-4 -top-4 glass px-3 py-2 flex items-center gap-2 gloss-highlight">
                <QrCode className="w-4 h-4" style={{ color: "var(--accent)" }} />
                <span className="text-xs">347 guests scanned</span>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-24 grid md:grid-cols-3 gap-4">
          {[
            { n: "01", t: "Design your page", d: "Names, date, cover image, monogram — a hand-crafted wedding invitation in minutes." },
            { n: "02", t: "Print your QR", d: "Place it on your table cards. Guests scan and land straight on your page." },
            { n: "03", t: "Collect memories", d: "Photos, videos, wishes — attached, organized, and never lost." },
          ].map((s) => (
            <div key={s.n} className="glass p-6" data-testid={`step-${s.n}`}>
              <div className="text-xs tracking-[0.3em] text-neutral-400">{s.n}</div>
              <div className="font-serif text-2xl mt-2">{s.t}</div>
              <div className="text-sm text-neutral-600 mt-2">{s.d}</div>
            </div>
          ))}
        </section>

        <section className="mt-24 glass p-8 sm:p-12 text-center gloss-highlight">
          <div className="font-serif text-3xl sm:text-4xl">Try three live demo weddings</div>
          <p className="text-neutral-600 mt-2 max-w-xl mx-auto">Each independently themed, with its own URL and QR. This is what your guests will see.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {[
              { slug: "sarah-john", label: "Sarah & John" },
              { slug: "emily-daniel", label: "Emily & Daniel" },
              { slug: "aisha-rayan", label: "Aisha & Rayan" },
            ].map((d) => (
              <Link key={d.slug} to={`/w/${d.slug}`} data-testid={`demo-${d.slug}`} className="btn-ghost-glass px-5 py-2 rounded-full text-sm">
                {d.label} →
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
